import { type ReactNode, forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import dayjs from "dayjs";

import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import { useDropdownDataProvider } from "~/features/orders/orderList/components/OrderListSearchBox/useDropdownDataProvider";
import { getDatesFromForm } from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox";
import { type AdvancedDateRangeValue } from "~/common/components/AdvancedDateRangePicker/AdvancedDateRangePicker";
import {
  QUERY_TYPE,
  type QueryType,
  type SearchByData,
  validateSearchQuery,
} from "~/features/orders/common/queryTypes";
import {
  OrderListSearchInput,
  makeLabelKeys,
} from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchInput";
import { Box, Grid, Tab, Tabs } from "~/common/mui";
import { ChipSelect } from "~/common/mui/Chip/ChipSelect";
import { AdvancedDateRangePicker } from "~/common/components/AdvancedDateRangePicker/AdvancedDateRangePicker";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";

import {
  getInitialAccounts,
  MultiAccountSelect,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSelect";
import { type SingleAccountData } from "~/features/orders/common/DownloadOrderHistoryService/types";
import { useTranslation } from "react-i18next";

import { Alert } from "~/common/mui/Alert/Alert";
import {
  deserializeDateRange,
  useFormPersistence,
} from "~/features/orders/orderList/components/OrderListSearchBox/searchFormPersistenceUtils";
import { useDesignToggle } from "~/features/orders/common/components/NewDesignToggle";

export interface OrderListSearchBoxProps {
  onSubmit: (formData: OrderListSearchBoxData) => void;
  variant: SearchBoxVariant;
  children?: ReactNode;
}

export interface OrderListSearchBoxHandle {
  reset: () => void;
}

interface SearchFormData {
  dateRange: AdvancedDateRangeValue;
  searchBy: SearchByData;
  statusOption: string;
  lineStatusOption: string[];
  accounts: string[];
}

const INITIAL_SEARCH_FORM_DATA: SearchFormData = {
  dateRange: { shortcut: "last30Days", dates: [dayjs().subtract(30, "day"), dayjs()] },
  searchBy: {
    query: "",
    type: QUERY_TYPE.PURCHASE_NO,
  },
  statusOption: "",
  lineStatusOption: [],
  accounts: getInitialAccounts(),
};

export interface OrderListSearchBoxData {
  query: string;
  queryType: QueryType;
  dateFrom?: Date;
  dateTo?: Date;
  isOrder: boolean;
  status?: string;
  lineStatus?: string[];
  accounts?: SingleAccountData[];
}

export const OrderListSearchBox = forwardRef<OrderListSearchBoxHandle, OrderListSearchBoxProps>(
  ({ onSubmit, variant, children }, ref) => {
    const { t } = useTranslation();
    const formHooks = useForm<SearchFormData>({
      defaultValues: INITIAL_SEARCH_FORM_DATA,
      mode: "onBlur",
    });
    const { isNewDesignEnabled } = useDesignToggle();

    const { orderStatuses, lineItemStatuses, loading: isDropdownDataLoading } = useDropdownDataProvider(variant);

    const storageKey = getStorageKey(variant);
    useFormPersistence<SearchFormData>(storageKey, formHooks.reset, formHooks.getValues, {
      version: 2,
      deserialize: (data) => ({
        ...(data as unknown as SearchFormData),
        dateRange: deserializeDateRange(data.dateRange as Record<string, unknown>),
      }),
    });

    const hasInitiallySubmitted = useRef(false);

    const onSubmitCb = useCallback(() => {
      const formData: SearchFormData = formHooks.getValues();
      const { searchBy, accounts: formAccounts } = formData;

      let accounts:
        | {
            accountNumber: string;
            endCustomer: string;
            salesOrg: string;
          }[]
        | undefined = undefined;
      if (formAccounts) {
        accounts = formAccounts
          .map((str) => str.split("-"))
          .map((parts) => ({
            accountNumber: parts[0] ?? "",
            endCustomer: parts[1] ?? "",
            salesOrg: parts[2] ?? "",
          }));
      }

      const requestPayload: OrderListSearchBoxData = {
        query: searchBy.query.trim(),
        queryType: searchBy.type,
        isOrder: true,
        status: formData.statusOption || undefined,
        lineStatus: formData.lineStatusOption,
        ...getDatesFromForm(formData.dateRange),
        accounts,
      };
      onSubmit(requestPayload);
    }, [formHooks, onSubmit]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          localStorage.removeItem(getStorageKey(variant));
          formHooks.reset(INITIAL_SEARCH_FORM_DATA);
          onSubmitCb();
        },
      }),
      [formHooks, onSubmitCb, variant],
    );

    useEffect(() => {
      if (!isDropdownDataLoading) {
        formHooks.handleSubmit(onSubmitCb)();
        hasInitiallySubmitted.current = true;
      }
    }, [formHooks, isDropdownDataLoading, onSubmitCb]);

    useEffect(() => {
      const subscription = formHooks.watch((_value, { name, type }) => {
        if (hasInitiallySubmitted.current && name && !name.startsWith("searchBy") && type === "change") {
          onSubmitCb();
        }
      });
      return () => subscription.unsubscribe();
    }, [formHooks, onSubmitCb]);

    if (isDropdownDataLoading) {
      return null;
    }

    const labelKeys = makeLabelKeys(variant);

    return (
      <FormProvider {...formHooks}>
        <form onSubmit={formHooks.handleSubmit(onSubmitCb)}>
          <Grid container columns={12} rowGap={3} columnGap={"var(--gutter)"}>
            <Grid size={6}>
              <Controller
                render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                  <OrderListSearchInput
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    onBlur={onBlur}
                    ref={ref}
                    variant={variant}
                  />
                )}
                name={"searchBy"}
                defaultValue={INITIAL_SEARCH_FORM_DATA.searchBy}
                control={formHooks.control}
                rules={{
                  validate: validateSearchQuery(t, labelKeys),
                }}
              />
            </Grid>
            <Grid size={3}>
              {isMultiAccountOrderSearchEnabled() && (
                <Controller
                  render={({ field: { value, onChange } }) => <MultiAccountSelect values={value} onChange={onChange} />}
                  name={"accounts"}
                  defaultValue={INITIAL_SEARCH_FORM_DATA.accounts}
                  control={formHooks.control}
                />
              )}
            </Grid>

            <Grid size={3}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <AdvancedDateRangePicker sx={{ width: "100%" }} value={value} onChange={onChange} />
                )}
                name={"dateRange"}
                control={formHooks.control}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Tabs value={value} onChange={(_, v) => onChange(v)} variant="fullWidth" fullWidthIndicator={false}>
                    {orderStatuses.map((entry) => (
                      <Tab key={entry.value} label={entry.label} value={entry.value} />
                    ))}
                  </Tabs>
                )}
                name={"statusOption"}
                control={formHooks.control}
              />
            </Grid>

            {!isNewDesignEnabled && (
              <Grid size={12} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Controller
                  render={({ field: { value, onChange } }) => (
                    <ChipSelect
                      multiple
                      value={value}
                      onChange={(_, v) => onChange(v)}
                      label={
                        variant === SearchBoxVariant.QUOTE
                          ? t("quoteList.quoteLineStatus")
                          : t("orderList.orderLineStatus")
                      }
                    >
                      {lineItemStatuses.map((entry) => (
                        <ChipSelect.Option key={entry.value} value={entry.value}>
                          {entry.label}
                        </ChipSelect.Option>
                      ))}
                    </ChipSelect>
                  )}
                  name={"lineStatusOption"}
                  control={formHooks.control}
                />
                <Box>{children}</Box>
              </Grid>
            )}

            {formHooks.watch("searchBy")?.type === QUERY_TYPE.SALES_NO && (
              <Grid size={12}>
                <Alert severity="info">{t("orderListPage.soInfo")}</Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    );
  },
);

OrderListSearchBox.displayName = "OrderListSearchBox";

const STORAGE_KEY_MAP = {
  [SearchBoxVariant.ORDER]: "orderHistorySearchData",
  [SearchBoxVariant.QUOTE]: "quoteHistorySearchData",
};

const getStorageKey = (variant: SearchBoxVariant) => {
  return STORAGE_KEY_MAP[variant];
};
